/**
 * JS replacement for the CSS color-mix() used by the design handoff.
 *
 * The project declares no browserslist, so it resolves to `defaults`, which still
 * includes chrome 109 and ios_saf 15.6 — neither supports color-mix(). An unsupported
 * color-mix() invalidates the whole `background` declaration, so a class would lose its
 * gradient entirely rather than degrade. Resolving the colours here removes the question,
 * and makes the composited result measurable (see the hero contrast check).
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

const clamp255 = (value: number): number => Math.min(255, Math.max(0, Math.round(value)));

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

export function parseHex(hex: string): Rgb {
  const raw = hex.trim().replace("#", "");
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw;

  if (!/^[0-9a-fA-F]{6}$/.test(full)) {
    throw new Error(`Invalid hex colour: ${hex}`);
  }

  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

export function toHex({ r, g, b }: Rgb): string {
  const part = (value: number): string => clamp255(value).toString(16).padStart(2, "0");
  return `#${part(r)}${part(g)}${part(b)}`;
}

export function toRgba({ r, g, b }: Rgb, alpha: number): string {
  const a = Math.round(clamp01(alpha) * 1000) / 1000;
  return `rgba(${clamp255(r)},${clamp255(g)},${clamp255(b)},${a})`;
}

const srgbToLinear = (channel: number): number => {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

const linearToSrgb = (channel: number): number => {
  const c = channel <= 0.0031308 ? 12.92 * channel : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055;
  return c * 255;
};

interface Oklab {
  L: number;
  a: number;
  b: number;
}

function rgbToOklab(rgb: Rgb): Oklab {
  const r = srgbToLinear(rgb.r);
  const g = srgbToLinear(rgb.g);
  const b = srgbToLinear(rgb.b);

  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  return {
    L: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  };
}

function oklabToRgb({ L, a, b }: Oklab): Rgb {
  const l = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3);
  const m = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3);
  const s = Math.pow(L - 0.0894841775 * a - 1.291485548 * b, 3);

  return {
    r: linearToSrgb(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    g: linearToSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    b: linearToSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  };
}

/**
 * Oklab in polar form: the same colour said as lightness, colourfulness and hue.
 *
 * Splitting hue from the other two is what lets a colour be adopted without being obeyed — keep
 * the hue someone chose, impose the L and C of the system, and every colour in the product carries
 * the same weight on screen whatever was picked.
 */
export interface Oklch {
  L: number;
  C: number;
  /** Degrees, 0–360. */
  h: number;
}

export function rgbToOklch(rgb: Rgb): Oklch {
  const { L, a, b } = rgbToOklab(rgb);
  const hue = (Math.atan2(b, a) * 180) / Math.PI;
  return { L, C: Math.hypot(a, b), h: hue < 0 ? hue + 360 : hue };
}

export function oklchToRgb({ L, C, h }: Oklch): Rgb {
  const radians = (h * Math.PI) / 180;
  return oklabToRgb({ L, a: C * Math.cos(radians), b: C * Math.sin(radians) });
}

/** color-mix(in srgb, base <ratio>%, other) — channel-wise lerp on gamma-encoded values. */
export function mixSrgb(base: string, ratio: number, other: string): string {
  const x = parseHex(base);
  const y = parseHex(other);
  const t = clamp01(ratio);
  return toHex({
    r: x.r * t + y.r * (1 - t),
    g: x.g * t + y.g * (1 - t),
    b: x.b * t + y.b * (1 - t),
  });
}

/**
 * color-mix(in srgb, base <ratio>%, transparent).
 * Mixing with transparent is premultiplied: the hue is kept and only alpha scales.
 */
export function fadeSrgb(base: string, ratio: number): string {
  return toRgba(parseHex(base), ratio);
}

/** color-mix(in oklab, base <ratio>%, other) — perceptual interpolation. */
export function mixOklab(base: string, ratio: number, other: string): string {
  const x = rgbToOklab(parseHex(base));
  const y = rgbToOklab(parseHex(other));
  const t = clamp01(ratio);
  return toHex(
    oklabToRgb({
      L: x.L * t + y.L * (1 - t),
      a: x.a * t + y.a * (1 - t),
      b: x.b * t + y.b * (1 - t),
    }),
  );
}

/** Relative luminance (WCAG 2.1) — used to check white text over a class hero. */
function relativeLuminance(rgb: Rgb): number {
  return (
    0.2126 * srgbToLinear(rgb.r) + 0.7152 * srgbToLinear(rgb.g) + 0.0722 * srgbToLinear(rgb.b)
  );
}

/** WCAG contrast ratio between two opaque colours. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [light, dark] = la >= lb ? [la, lb] : [lb, la];
  return (light + 0.05) / (dark + 0.05);
}